import { required, requiredIf, minLength, maxLength, email } from 'vuelidate/lib/validators'
import { guidValidator, abnValidator } from '@/components/validators'
import { DateTime } from 'luxon'
import merge from 'lodash/merge'
import QuoteOrmMixin, { ormValidationRules } from './QuoteOrmValidation'

const dateValidator = function (value) {
  if (value && value !== '__/__/____') {
    const date = DateTime.fromISO(value)
    return date.isValid
  } else {
    return value !== undefined
  }
}

const customMinLength = (value, vehicle) => {
  if (vehicle && vehicle.bodyId && vehicle.bodyId.toUpperCase() === 'O') {
    return true
  } else if (value !== null || value != undefined) {
    return value.length >= 7 || value.length === 0
  }
  return true
}

const baseValidationRules = {
    detailGroup: [
      'entity.quoteId',
      'entity.vehicleId',
      'entity.insurerId',
      'entity.quoter',
      'entity.authorisedDate'
    ],
    vehicleCustomerGroup: ['entity.customerId'],
    vehicleCustomerIsActiveGroup: ['quoteCustomer.active', 'quoteVehicle.isActive'],
    quoteHeaderGroup: [
      'entity.quoteId',
      'entity.vehicleId',
      'entity.insurerId',
      'vehicle.rego',
      'vehicle.model',
      'vehicle.bodyId',
      'vehicle.bodyId2',
      'vehicle.paintGroup',
      'customer.lastname',
      'customer.firstname',
      'customer.companyName',
      'customer.emails',
      'customer.contacts',
      'insurer.name'
    ],
    audaNetHeaderGroup: ['entity.claimNo'],
    entity: {
      quoteId: {
        required,
        validGuid: guidValidator
      },
      quoter: {
        required
      },
      vehicleId: {
        required,
        validGuid: guidValidator
      },
      customerId: {
        required,
        validGuid: guidValidator,
        sameAsVehicle: function (value) {
          return this.vehicle && this.vehicle.customer ? value === this.vehicle.customer.key : true
        }
      },
      insurerId: {
        required,
        validGuid: guidValidator
      },
      authorisedDate: {
        validDate: dateValidator
      },
      labours: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          }
        }
      },
      others: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          }
        }
      },
      parts: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          partNo: {
            maxLength: maxLength(24)
          }
        }
      },
      opgs: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          }
        }
      },
      miscs: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          }
        }
      },
      sublets: {
        $each: {
          itemNo: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          },
          itemDesc: {
            required: requiredIf(function (model) {
              return !model.deleted
            })
          }
        }
      },
      invoices: {
        $each: {
          assetId: {
            required,
            validGuid: guidValidator
          }
        }
      },
      claimNo: {
        required: requiredIf(function () {
          return this.entity.audatex !== null && this.entity.audatex !== undefined
        })
      }
    },
    vehicle: {
      rego: {
        required
      },
      customer: {
        required
      },
      model: {
        required
      },
      bodyId: {
        required
      },
      bodyId2: {},
      paintGroup: {
        required
      },
      vin: {
        customMinLength: customMinLength,
        maxLength: maxLength(17)
      },
      id: {
        required,
        validGuid: guidValidator
      },
      buildMonth: {},
      buildYear: {},
      colour: {
        required
      },
      transmissionType: {
        required
      }
    },
    customer: {
      companyName: {
        required: requiredIf(function (model) {
          return !model.isPerson
        }),
        isNotWhitespaceOnly: function (value, model) {
          if (model.isPerson) return true
          return typeof value === 'string' && value.trim().length > 0
        }
      },
      firstname: {
        required: requiredIf(function (model) {
          return model.isPerson
        }),
        isNotWhitespaceOnly: function (value, model) {
          if (!model.isPerson) return true
          return typeof value === 'string' && value.trim().length > 0
        }
      },
      lastname: {
        required: requiredIf(function (model) {
          return model.isPerson
        }),
        isNotWhitespaceOnly: function (value, model) {
          if (!model.isPerson) return true
          return typeof value === 'string' && value.trim().length > 0
        }
      },
      emails: {
        $each: {
          address: {
            email
          }
        }
      },
      contacts: {
        $each: {
          contact: {
            firstName: {
              required: requiredIf(function (contactWrapper) {
                return true
                console.log(contactWrapper)
                return contactWrapper.isDefault === true && contactWrapper.isDeleted === false
              })
            },
            lastName: {
              required: requiredIf(function (contactWrapper) {
                return true
                return contactWrapper.isDefault === true && contactWrapper.isDeleted === false
              })
            }
          }
        }
      }
    },
    insurer: {
      insurerId: {
        required,
        validGuid: guidValidator
      },
      name: {
        required
      },
      abn: {
        minLength: minLength(11),
        validAbn: abnValidator
      },
      arbn: {
        minLength: minLength(9)
      }
    },
    quoteCustomer: {
      active: {
        required,
        isActive: (value) => value === true
      }
    },
    quoteVehicle: {
      isActive: {
        required,
        isActive: (value) => value === true
      }
    }
}

export default {
  mixins: [QuoteOrmMixin],
  provide: function () {
    return {
      $vv: this.$v
    }
  },
  validations: merge({}, baseValidationRules, ormValidationRules)
}
